import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Factory, Fuel, Building2, Flame } from "lucide-react";
import { OPPORTUNITIES } from "../data/opportunities";

const ICONS = {
  "cbg-plant": Factory,
  "petrol-pump": Fuel,
  "sub-dealer": Building2,
  "petrol-diesel-2-2": Fuel,
  "petrol-diesel-4-2": Fuel,
  "petrol-diesel-noc-cng": Fuel,
  "petrol-diesel-cng": Fuel,
  "only-cng": Flame,
};

export default function Opportunities() {
  const [active, setActive] = useState("franchise");
  const navigate = useNavigate();

  const group = useMemo(() => OPPORTUNITIES[active], [active]);

  return (
    <section
      id="opportunities"
      className="relative py-28 md:py-36 overflow-x-hidden"
    >
      {/* Background blobs */}
      <div
        className="eco-blob bg-eco-light"
        style={{
          width: 520,
          height: 520,
          top: "10%",
          left: "-12%",
          opacity: 0.18,
          pointerEvents: "none",
        }}
      />
      <div
        className="eco-blob bg-saffron"
        style={{
          width: 360,
          height: 360,
          bottom: "0%",
          right: "-5%",
          opacity: 0.14,
          pointerEvents: "none",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">

        {/* HEADER */}
        <div className="flex items-end justify-between flex-wrap gap-8">
          <div className="max-w-2xl">
            <span className="overline">Franchise · Dealership</span>

            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mt-5 leading-[1.05]">
              Own a piece of the <br />
              <span className="gradient-text-eco">
                green energy economy
              </span>
            </h2>

            <p className="mt-6 text-white/65">
              Choose the model that fits your land, capital and ambition.
            </p>
          </div>

          {/* TOGGLE */}
          <div className="glass-card p-1.5 inline-flex gap-1">
            {Object.values(OPPORTUNITIES).map((g) => (
              <button
                key={g.id}
                onClick={() => setActive(g.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  active === g.id
                    ? "bg-white text-black"
                    : "text-white/60 hover:text-white"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        {/* GROUP INFO */}
        <div className="mt-12 glass-card p-7 md:p-10 relative overflow-hidden">

          <div
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-25"
            style={{ background: group.accent }}
          />

          <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-6">

            <div>
              <div
                className="font-mono-eco text-[11px] uppercase"
                style={{ color: group.accent }}
              >
                {group.label} Program
              </div>

              <h3 className="font-display text-2xl md:text-3xl font-bold mt-2">
                {group.title}
              </h3>

              <p className="text-white/65 mt-3 max-w-2xl">
                {group.tagline}
              </p>
            </div>

            <div className="text-sm text-white/55 max-w-md">
              {group.desc}
            </div>
          </div>

          {/* Dealer extra info */}
          {active === "dealership" && (
            <div className="mt-6 grid md:grid-cols-3 gap-4 text-sm">
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="font-semibold">Low Investment</div>
                <div className="text-white/60">Start with minimal setup cost</div>
              </div>

              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="font-semibold">Fast Setup</div>
                <div className="text-white/60">Operational in weeks</div>
              </div>

              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="font-semibold">High Demand</div>
                <div className="text-white/60">Pre-approved zones</div>
              </div>
            </div>
          )}
        </div>

        {/* OPTIONS - UPDATED TO A 2x2 MATRIX FOR TABLETS & DESKTOPS */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-6xl">
          {group.options.map((opt, i) => {
            const Icon = ICONS[opt.id] || Fuel;

            return (
              <div
                key={opt.id}
                onClick={() =>
                  navigate(`/enquiry?type=${group.id}&option=${opt.id}`)
                }
                className="group glass-card p-7 cursor-pointer hover:scale-[1.01] transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between mb-6">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{
                        background: `${group.accent}22`,
                        border: `1px solid ${group.accent}55`,
                      }}
                    >
                      <Icon
                        className="w-5 h-5"
                        style={{ color: group.accent }}
                      />
                    </div>

                    <span className="text-white/30 text-xs font-mono-eco">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <div className="text-xl font-semibold">
                    {opt.title}
                  </div>

                  <div className="text-xs uppercase text-white/40 mt-1">
                    {opt.subtitle}
                  </div>

                  <p className="text-sm text-white/60 mt-4">
                    {opt.desc}
                  </p>
                </div>

                <div className="mt-6 flex justify-end">
                  <span className="flex items-center gap-1 text-sm text-white/70 group-hover:text-orange-400 transition-colors">
                    Enquire <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 glass-card p-7 flex justify-between items-center flex-wrap gap-4">

          <div>
            <div className="text-xl font-semibold">
              Not sure which model fits you?
            </div>
            <div className="text-sm text-white/60">
              We’ll help you choose the right opportunity.
            </div>
          </div>

          <button
            onClick={() => navigate("/enquiry")}
            className="px-5 py-2.5 bg-orange-500 text-black rounded-full font-semibold"
          >
            General Enquiry <ArrowUpRight className="inline w-4 h-4" />
          </button>

        </div>

      </div>
    </section>
  );
}